"use client";

import React, { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const coveredAreas = ["Splendora, TX", "New Caney, TX", "Porter, TX", "Cleveland, TX", "Kingwood, TX", "Atascocita, TX", "Humble, TX", "Roman Forest, TX", "Woodbranch, TX", "Patton Village, TX"];
export default function ServiceAreasMap() {
  const {
    ref: leftRef,
    isVisible: isLeftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: isRightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="113740" id="service-areas-map-list" className="bg-background py-24 overflow-hidden text-foreground">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          {}
           <div ref={leftRef} className="relative w-full h-[400px] lg:h-[600px] rounded-xl shadow-xl overflow-hidden bg-muted transition-all duration-1000 ease-out opacity-100 translate-x-0 !bg-[url(https://schaefergaragedoors.com/wp-content/uploads/2026/04/660758829_122120368395209682_2818658536071485800_n-e1775081291220.jpg)]">
            {}
            
            
            {}
            <div className="absolute inset-0 bg-primary/5 pointer-events-none backdrop-blur-[1px]"></div>
          </div>

          {}
          <div ref={rightRef} className={`flex flex-col justify-center transition-all duration-1000 delay-200 ease-out ${isRightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-12"}`}>
            <div className="mb-10">
              <h2 className="text-4xl md:text-5xl font-bold text-foreground tracking-tight mb-4 uppercase font-serif">
                Texas Service Areas
              </h2>
              <div className="h-[4px] w-[60px] bg-primary rounded-full"></div>
              <p className="mt-6 text-xl text-muted-foreground leading-relaxed">
                Schaefer Garage Doors delivers veteran-owned professionalism across Kingwood, TX and surrounding areas. We are equipped for 24/7 emergency repairs and  same-day service.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
              {coveredAreas.map((area, index) => {
              const cityUrlMap = {
                "Splendora, TX": "/service-areas/garage-door-repair-in-splendora-tx/",
                "New Caney, TX": "/service-areas/garage-door-repair-in-new-caney-tx/",
                "Porter, TX": "/service-areas/garage-door-repair-in-porter-tx/",
                "Cleveland, TX": "/service-areas/garage-door-repair-in-cleveland-tx/",
                "Kingwood, TX": "/service-areas/garage-door-repair-in-kingwood-tx/",
                "Atascocita, TX": "/service-areas/garage-door-repair-in-atascocita-tx/",
                "Humble, TX": "/service-areas/garage-door-repair-in-humble-tx/",
                "Roman Forest, TX": "/service-areas/garage-door-repair-in-roman-forest-tx/",
                "Woodbranch, TX": "/service-areas/garage-door-repair-in-woodbranch-tx/",
                "Patton Village, TX": "/service-areas/garage-door-repair-in-patton-village-tx/"
              };
              const cityUrl = cityUrlMap[area];
              return <div key={index} data-index={index} className="flex items-start gap-3 group">
                    <div className="mt-0.5 bg-primary/10 p-1 rounded-md shrink-0 transition-colors group-hover:bg-primary/20">
                      <Check className="size-4 text-primary" strokeWidth={3} />
                    </div>
                    {cityUrl ? <Link to={cityUrl} className="text-base font-semibold text-foreground/80 group-hover:text-primary transition-colors">
                        {area}
                      </Link> : <span className="text-base font-semibold text-foreground/80 group-hover:text-primary transition-colors">
                        {area}
                      </span>}
                  </div>;
            })}
            </div>
          </div>

        </div>
      </div>
    </section>;
}